<template>
  <q-page class="q-pa-sm">
    <div class="row q-col-gutter-sm">
      <div class="col-lg-12 col-md-12 col-xs-12 col-sm-12">
        <q-card class="card-bg no-shadow" bordered>
          <q-form @reset="onReset" @submit="onSave">
            <q-card-section class="card-bg-title">
              <div class="text-h6">Add token</div>
              <div class="text-subtitle2">supports any Fido2 devices (software/hardware)</div>
            </q-card-section>
            <q-card-section class="q-pa-sm">
              <q-item>
                <q-item-section class="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                  <q-input
                    v-model="name"
                    :rules="[(val) => (val && val.length > 0) || $t('errTypeSomething')]"
                    filled
                    :placeholder="$t('deviceName')"
                    lazy-rules
                    bordered
                    dense
                    dark
                    q-input />
                </q-item-section>
              </q-item>
            </q-card-section>
            <q-card-actions align="left" class="q-pl-lg">
              <q-btn color="primary" :label="$t('add')" type="submit" />
              <q-btn class="q-ml-sm" flat :label="$t('reset')" type="reset" />
            </q-card-actions>
          </q-form>
        </q-card>
      </div>
      <div class="col-lg-12 col-md-12 col-xs-12 col-sm-12">
        <q-card class="card-bg no-shadow" bordered>
          <q-card-section class="card-bg-title">
            <div class="text-h6">{{ $t('TFA devices') }}</div>
          </q-card-section>
          <q-table
            class="sticky-table-header row"

            ref="tableRef"
            row-key="index"
            selection="multiple"

            v-model:selected="selected"
            v-model:pagination="pagination"

            binary-state-sort flat bordered virtual-scroll

            :grid="$q.screen.xs"
            :rows="rows"
            :columns="TFA.columns"
            :loading="loading"
            :visible-columns="TFA.visible"

            :virtual-scroll-item-size="itemSize"
            :virtual-scroll-sticky-size-start="itemSize"
            :rows-per-page-options="[0]"

            @virtual-scroll="onScroll"
          >
            <template v-slot:top>
              <q-btn v-if="rows.length !== 0" class="q-ml-sm" color="secondary" filled
                     :disable="loading || selected.length == 0"
                     :label="$t(`remove.row${selected.length > 1?'s':''}`)"
                     @click="onRemove" />
              <q-space />
              <q-input borderless dense debounce="300" color="secondary" filled v-model="filter">
                <template v-slot:append>
                  <q-icon name="mdi-magnify" color="white" />
                </template>
              </q-input>
            </template>
          </q-table>
        </q-card>
      </div>
    </div>
  </q-page>
</template>

<script lang="ts" setup>
import { type QTable, useQuasar } from 'quasar';
import { onMounted, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Delete, Load, Submit, TFA } from '@/service/tfa';
import type { Pagination, Scroll } from '@/types/option';
import { Reset } from '@/service';

const $q = useQuasar();
const { t } = useI18n();

const pageSize = 20;
const itemSize = 48;

const tableRef = ref() as Ref<QTable>;
const loading = ref(false);
const loaded = ref(false);
const rows = ref([]);
const selected = ref([]);
const filter = ref('');
const pagination = ref({ rowsPerPage: 0, rowsNumber: 0 } as Pagination);

const name = ref('');

const onReset = () => {
  Reset([name]);
};
const onSave = () => {
  Submit(name.value).then(response).catch(error);
};

const onScroll = async (scroll: Scroll) => {
  await Load(rows, pagination, loading, loaded, pageSize, false, scroll);
};

const onRemove = async () => {
  await Delete(selected.value, loading).then(response).catch(error);
};

onMounted(async () => {
  await Load(rows, pagination, loading, loaded, pageSize, true);
});

async function reload() {
  loaded.value = false;
  rows.value = [];
  pagination.value.lastId = null;
  await Load(rows, pagination, loading, loaded, pageSize, true);
  tableRef.value.resetVirtualScroll();
}

async function response(response: Response) {
  if (!response.ok) {
    return Promise.reject(Error(response.statusText));
  }
  $q.notify({
    color: 'green-4',
    icon: 'mdi-check-circle',
    message: t('successful.saved')
  });
  await reload();
}

function error(error: Error) {
  $q.notify({
    color: 'red-4',
    icon: 'mdi-alert-circle',
    message: t(error.toString())
  });
}
</script>
